/**
 * Toolbar for the Accounting Recurring transactions page.
 *
 * Sits inside the sticky chrome in index.tsx so it stays pinned
 * with the toolbar + table column header row as the user scrolls.
 *
 * Left side: pill search bar + Filter button + filter summary tag.
 * Right side: Print icon + Settings icon (gear).
 *
 * The Settings button accepts an optional `settingsButtonRef` so
 * the parent can anchor a popover (CustomizeColumnsPopover) to it.
 * The ref type allows null to match what `useRef<HTMLButtonElement>(null)`
 * returns under strict TypeScript.
 */

import type { CSSProperties, RefObject } from "react";
import { Search, Filter, Printer, Settings } from "lucide-react";

export interface RecurringToolbarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  onFilterClick: () => void;
  filterSummary: string;
  hasActiveFilters: boolean;
  onPrintClick: () => void;
  onCustomizeClick: () => void;
  /** Optional ref forwarded to the Settings (gear) button for popover anchoring. */
  settingsButtonRef?: RefObject<HTMLButtonElement | null>;
}

export function RecurringToolbar({
  searchValue,
  onSearchChange,
  onFilterClick,
  filterSummary,
  hasActiveFilters,
  onPrintClick,
  onCustomizeClick,
  settingsButtonRef,
}: RecurringToolbarProps) {
  return (
    <div style={toolbarStyle}>
      {/* Pill search bar (300px, same style as Rules + Integration transactions) */}
      <div style={searchWrapStyle}>
        <Search size={15} style={searchIconStyle} />
        <input
          type="text"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search by name"
          style={searchInputStyle}
        />
      </div>

      {/* Filter button */}
      <button
        type="button"
        onClick={onFilterClick}
        style={{
          ...filterBtnStyle,
          ...(hasActiveFilters ? filterBtnActiveStyle : {}),
        }}
      >
        <Filter size={14} strokeWidth={2} />
        Filter
      </button>

      {/* Filter summary tag (shows "All" when no filters active) */}
      <span style={filterTagStyle}>{filterSummary}</span>

      {/* Right side: Print + Settings (gear) */}
      <div style={rightStyle}>
        <button
          type="button"
          onClick={onPrintClick}
          style={iconBtnStyle}
          aria-label="Print list"
          title="Print"
        >
          <Printer size={16} strokeWidth={2} />
        </button>
        <button
          ref={settingsButtonRef}
          type="button"
          onClick={onCustomizeClick}
          style={iconBtnStyle}
          aria-label="Settings"
          title="Settings"
        >
          <Settings size={16} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}

// ---------- Styles ----------

const toolbarStyle: CSSProperties = {
  padding: "12px 28px",
  display: "flex",
  gap: 10,
  alignItems: "center",
  background: "#ffffff",
  borderBottom: "1px solid #E7EAF0",
};

const searchWrapStyle: CSSProperties = {
  position: "relative",
  width: 300,
  maxWidth: "100%",
  display: "inline-flex",
  alignItems: "center",
};

const searchIconStyle: CSSProperties = {
  position: "absolute",
  left: 14,
  top: "50%",
  transform: "translateY(-50%)",
  color: "#6B7684",
  pointerEvents: "none",
};

const searchInputStyle: CSSProperties = {
  width: "100%",
  padding: "9px 16px 9px 38px",
  border: "1px solid #D8DEE6",
  borderRadius: 999,
  fontSize: 13,
  fontWeight: 500,
  color: "#0E1A1A",
  background: "#ffffff",
  outline: "none",
  fontFamily: "inherit",
};

const filterBtnStyle: CSSProperties = {
  padding: "8px 14px",
  border: "1px solid #D8DEE6",
  borderRadius: 8,
  background: "#ffffff",
  cursor: "pointer",
  fontSize: 12.5,
  color: "#0E1A1A",
  fontWeight: 600,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  fontFamily: "inherit",
};

const filterBtnActiveStyle: CSSProperties = {
  background: "#0E1A1A",
  color: "#ffffff",
  borderColor: "#0E1A1A",
};

const filterTagStyle: CSSProperties = {
  fontSize: 12.5,
  color: "#3A4A52",
  fontWeight: 500,
};

const rightStyle: CSSProperties = {
  marginLeft: "auto",
  display: "flex",
  gap: 8,
  alignItems: "center",
};

const iconBtnStyle: CSSProperties = {
  width: 30,
  height: 30,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 6,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  color: "#3A4A52",
  fontFamily: "inherit",
};

export default RecurringToolbar;